import api from './client';

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

// Load the Razorpay web checkout script once, lazily.
let scriptPromise = null;
function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = CHECKOUT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => { scriptPromise = null; resolve(false); };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Run Razorpay checkout for a booking's current due amount — the web twin of
 * the app's src/api/payment.js, with identical semantics:
 *   1. ask the server to create an order (it decides the amount)
 *   2. open checkout (or short-circuit for ₹0 / dev-mode orders)
 *
 * Returns { success, paymentId, fields, error }. The caller hands `fields` to
 * /pay-stage, which verifies the signature server-side and advances the stage.
 */
export async function payForBooking(bookingId, { name, prefill } = {}) {
  // 1. Create the order on the server.
  let order;
  try {
    order = await api.post(`/bookings/${bookingId}/create-order`);
  } catch (e) {
    // Offline / demo — mirror the app so the flow stays testable.
    const id = 'pay_demo_' + Date.now();
    return { success: true, demo: true, paymentId: id, fields: { transactionId: id } };
  }

  // Nothing to charge (₹0 stage).
  if (order.free || !(order.amount > 0)) return { success: true, free: true, fields: {} };

  // 2. Real checkout — needs live keys from admin settings.
  if (!order.dev && order.keyId) {
    const ready = await loadRazorpay();
    if (!ready || !window.Razorpay) {
      return { success: false, error: 'Could not load the payment gateway. Please check your connection and retry.' };
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = (v) => { if (!settled) { settled = true; resolve(v); } };

      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount, // paise, from the server
        currency: order.currency || 'INR',
        name: 'Narayan',
        description: name || order.label || 'Service payment',
        order_id: order.orderId,
        theme: { color: '#C1121F' },
        prefill: {
          name: prefill?.name || '',
          contact: prefill?.mobile || '',
          email: prefill?.email || '',
        },
        modal: {
          ondismiss: () => finish({ success: false, error: 'Payment cancelled' }),
        },
        handler: (data) =>
          finish({
            success: true,
            paymentId: data.razorpay_payment_id,
            fields: {
              razorpay_order_id: data.razorpay_order_id,
              razorpay_payment_id: data.razorpay_payment_id,
              razorpay_signature: data.razorpay_signature,
            },
          }),
      });

      rzp.on('payment.failed', (resp) =>
        finish({ success: false, error: resp?.error?.description || 'Payment failed' })
      );

      rzp.open();
    });
  }

  // Server is in dev mode (no keys) — simulate success so the flow is testable.
  const devId = 'pay_dev_' + Date.now();
  return { success: true, demo: true, paymentId: devId, fields: { razorpay_order_id: order.orderId, razorpay_payment_id: devId } };
}

const payment = { payForBooking };
export default payment;
